import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { BuyerService } from '../../../core/api/buyer.service';
import { AddDocusignDialogComponent } from '../../../shared/dialogs/add-docusign-dialog/add-docusign-dialog.component';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Buyer, User } from '../../../shared/models';
import { DocusignTemplate } from '../../../shared/models/docusign-template.model';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import * as fromApp from '../../../store/index';
import { AdminTableBase } from '../admin-table-base/admin-table-base';

@Component({
  selector: 'fc-docusign-page',
  templateUrl: './manage-docusign-page.component.html',
  styleUrls: ['./manage-docusign-page.component.scss'],
})
export class ManageDocusignPageComponent extends AdminTableBase implements OnInit {
  destroyRef = inject(DestroyRef);
  displayedColumns: string[] = ['actions', 'description', 'buyer'];

  readonly PAGE_NAME: string = 'docusign-page';

  filterValues = {
    buyer: null,
  };

  dataSource: MatTableDataSource<DocusignTemplate>;
  buyers: Buyer[];

  constructor(
    readonly router: Router,
    public dialog: MatDialog,
    readonly store: Store<fromApp.AppState>,
    readonly buyerService: BuyerService
  ) {
    super();
    this.buyerService.getAllBuyers().subscribe(buyers => {
      this.buyers = buyers;
    });
  }

  ngOnInit() {
    this.store
      .select(selectAdminFiltersAndPage)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        let pageFilter = super.setUpAdminFilter(state);
        this.filterQuery = pageFilter?.filterQuery || '';
        this.initializeFilters();
        this.queryItems();
      });
  }

  queryItems(): void {
    this.buyerService.getEdocTemplates('?' + this.filterQuery).subscribe(templateResults => {
      this.dataSource = new MatTableDataSource(templateResults);
    });
  }

  clickRow(row: DocusignTemplate): void {
    this.openDialog({
      buyers: this.buyers,
      dialogAction: 'Update',
      template: row,
    });
  }

  addNew() {
    this.openDialog({
      buyers: this.buyers,
      dialogAction: 'Create',
      template: null,
    });
  }

  openDialog(data: any) {
    this.dialog
      .open(AddDocusignDialogComponent, {
        data: data,
      })
      .afterClosed()
      .subscribe((user: User) => {
        if (user) {
          this.queryItems();
        }
      });
  }

  deleteTemplate(element: DocusignTemplate) {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Delete Confirmation',
          line1: 'Are you sure you want to delete this template? This action cannot be undone.',
          line2: 'Description: ' + element.description,
          line3: 'Buyer: ' + element.buyer.name,
          buttonTextYes: 'Yes, Delete',
          buttonTextNo: 'Cancel',
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.buyerService.deleteDocusignTemplate(element.id).subscribe(() => {
            this.queryItems();
          });
        }
      });
  }
}
